"use client";

import { useState, useEffect } from "react";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  TransitionChild,
} from "@headlessui/react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import {
  Bars3Icon,
  XMarkIcon,
  TrashIcon,
  ArrowPathIcon,
  LinkIcon,
  CalendarDateRangeIcon,
  VideoCameraIcon,
  ChartBarIcon,
  CheckBadgeIcon,
  ExclamationTriangleIcon,
  Squares2X2Icon,
} from "@heroicons/react/24/outline";
import { UserButton, useUser, useAuth } from "@clerk/clerk-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import RemoveChannelConfirm from "./RemoveChannelConfirm.jsx";

import logo from "../assets/logo.png";

const apiBaseUrl = import.meta.env.VITE_API_URL;

const navigation = [
  { name: "Videos", href: "/", icon: VideoCameraIcon, current: true },
  { name: "Blocks", href: "/blocks", icon: Squares2X2Icon, current: false },
  { name: "Links", href: "/links", icon: LinkIcon, current: false },
  { name: "Stats", href: "/stats", icon: ChartBarIcon, current: false },
  {
    name: "Pending",
    href: "/pending",
    icon: CalendarDateRangeIcon,
    current: false,
  },
  { name: "History", href: "/history", icon: ArrowPathIcon, current: false },
];

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

function Sidebar({ children, channel }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showRemoveChannelConfirm, setShowRemoveChannelConfirm] =
    useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [errorCode, setErrorCode] = useState(null);

  const { user } = useUser();
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const searchParams = useSearch({ strict: false });
  const queryClient = useQueryClient();

  useEffect(() => {
    // Check for error query parameters
    const error = searchParams?.error;
    const message = searchParams?.error_message;

    if (error) {
      setErrorCode(error);
      setErrorMessage(message || "An error occurred during authentication");

      // Clear the error parameters from the URL
      navigate({ search: {} }, { replace: true });
    }
  }, [searchParams, navigate]);

  const mutation = useMutation({
    mutationFn: async (data) => await removeChannel(),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["channelInfo"] });
    },
  });

  async function removeChannel() {
    const token = await getToken();

    return fetch(`${apiBaseUrl}/remove-channel/`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  const handleRemoveChannel = () => {
    mutation.mutate();
  };

  const onConnectClick = async () => {
    const token = await getToken();
    window.location.href = `${apiBaseUrl}/auth/google/?token=${token}`;
  };

  const dismissError = () => {
    setErrorMessage(null);
    setErrorCode(null);
  };

  const getErrorDetails = () => {
    switch (errorCode) {
      case "no_channel_found":
        return {
          title: "No YouTube Channel Found",
          showFullMessage: true,
        };
      case "channel_mismatch":
        return {
          title: "Wrong YouTube Channel",
          showFullMessage: true,
        };
      default:
        return {
          title: "Authentication Error",
          showFullMessage: true,
        };
    }
  };

  return (
    <>
      {/*
        This example requires updating your template:

        ```
        <html class="h-full bg-white dark:bg-gray-900">
        <body class="h-full">
        ```
      */}
      <div>
        <Dialog
          open={sidebarOpen}
          onClose={setSidebarOpen}
          className="relative z-50 lg:hidden"
        >
          <DialogBackdrop
            transition
            className="fixed inset-0 bg-gray-900/80 transition-opacity duration-300 ease-linear data-closed:opacity-0"
          />

          <div className="fixed inset-0 flex">
            <DialogPanel
              transition
              className="relative mr-16 flex w-full max-w-xs flex-1 transform transition duration-300 ease-in-out data-closed:-translate-x-full"
            >
              <TransitionChild>
                <div className="absolute top-0 left-full flex w-16 justify-center pt-5 duration-300 ease-in-out data-closed:opacity-0">
                  <button
                    type="button"
                    onClick={() => setSidebarOpen(false)}
                    className="-m-2.5 p-2.5"
                  >
                    <span className="sr-only">Close sidebar</span>
                    <XMarkIcon
                      aria-hidden="true"
                      className="size-6 text-white"
                    />
                  </button>
                </div>
              </TransitionChild>

              {/* Sidebar component, swap this element with another sidebar if you like */}
              <div className="relative flex grow flex-col gap-y-5 overflow-y-auto bg-white px-6 pb-2 dark:bg-gray-900 dark:ring dark:ring-white/10 dark:before:pointer-events-none dark:before:absolute dark:before:inset-0 dark:before:bg-black/10">
                <div className="relative flex h-16 shrink-0 items-center">
                  <img
                    alt="Your Company"
                    src={logo}
                    className="h-8 w-auto dark:hidden"
                  />
                  <img
                    alt="Your Company"
                    src={logo}
                    className="h-8 w-auto not-dark:hidden"
                  />
                </div>
                <nav className="relative flex flex-1 flex-col">
                  <ul role="list" className="flex flex-1 flex-col gap-y-7">
                    <li>
                      <ul role="list" className="-mx-2 space-y-1">
                        {navigation.map((item) => (
                          <li key={item.name}>
                            <Link
                              to={item.href}
                              className={classNames(
                                "group flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold",
                              )}
                              activeProps={{
                                className:
                                  "bg-gray-50 text-indigo-600 dark:bg-white/5 dark:text-white",
                              }}
                              inactiveProps={{
                                className:
                                  "text-gray-700 hover:bg-gray-50 hover:text-indigo-600 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-white",
                              }}
                            >
                              <item.icon
                                aria-hidden="true"
                                className={classNames(
                                  item.current
                                    ? "text-indigo-600 dark:text-white"
                                    : "text-gray-400 group-hover:text-indigo-600 dark:group-hover:text-white",
                                  "size-6 shrink-0",
                                )}
                              />
                              {item.name}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </li>
                  </ul>
                </nav>
              </div>
            </DialogPanel>
          </div>
        </Dialog>

        {/* Static sidebar for desktop */}
        <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col dark:bg-gray-900">
          {/* Sidebar component, swap this element with another sidebar if you like */}
          <div className="flex grow flex-col gap-y-5 overflow-y-auto border-r border-gray-200 bg-white px-6 dark:border-white/10 dark:bg-black/10">
            <div className="flex h-16 shrink-0 items-center">
              <img
                alt="Your Company"
                src={logo}
                className="h-8 w-auto dark:hidden"
              />
              <img
                alt="Your Company"
                src={logo}
                className="h-8 w-auto not-dark:hidden"
              />
            </div>
            <nav className="flex flex-1 flex-col">
              <ul role="list" className="flex flex-1 flex-col gap-y-7">
                <li>
                  <ul role="list" className="flex flex-1 flex-col gap-y-7">
                    <li>
                      <ul role="list" className="-mx-2 space-y-1">
                        {navigation.map((item) => (
                          <li key={item.name}>
                            <Link
                              to={item.href}
                              className={classNames(
                                "group flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold",
                              )}
                              activeProps={{
                                className:
                                  "bg-gray-50 text-indigo-600 dark:bg-white/5 dark:text-white",
                              }}
                              inactiveProps={{
                                className:
                                  "text-gray-700 hover:bg-gray-50 hover:text-indigo-600 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-white",
                              }}
                            >
                              <item.icon
                                aria-hidden="true"
                                className={classNames(
                                  item.current
                                    ? "text-indigo-600 dark:text-white"
                                    : "text-gray-400 group-hover:text-indigo-600 dark:group-hover:text-white",
                                  "size-6 shrink-0",
                                )}
                              />
                              {item.name}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </li>
                  </ul>
                </li>
                <li className="-mx-6 mt-auto">
                  <div className="flex items-center gap-x-4 px-6 py-3 text-sm/6 font-semibold text-gray-900 hover:bg-gray-50 dark:text-white dark:hover:bg-white/5">
                    <UserButton>
                      <UserButton.MenuItems>
                        {!channel?.has_all_scopes && (
                          <UserButton.Action
                            label="Grant additional access"
                            labelIcon={<CheckBadgeIcon />}
                            onClick={onConnectClick}
                          />
                        )}
                        <UserButton.Action
                          label="Unlink YouTube Channel"
                          labelIcon={<TrashIcon />}
                          onClick={() => setShowRemoveChannelConfirm(true)}
                        />
                      </UserButton.MenuItems>
                    </UserButton>
                    <span className="sr-only">Your profile</span>
                    <span aria-hidden="true">
                      {user && user.primaryEmailAddress?.emailAddress}
                    </span>
                  </div>
                </li>
              </ul>
            </nav>
          </div>
        </div>

        <div className="sticky top-0 z-40 flex items-center gap-x-6 bg-white px-4 py-4 shadow-xs sm:px-6 lg:hidden dark:bg-gray-900 dark:shadow-none dark:after:pointer-events-none dark:after:absolute dark:after:inset-0 dark:after:border-b dark:after:border-white/10 dark:after:bg-black/10">
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            className="-m-2.5 p-2.5 text-gray-700 hover:text-gray-900 lg:hidden dark:text-gray-400 dark:hover:text-white"
          >
            <span className="sr-only">Open sidebar</span>
            <Bars3Icon aria-hidden="true" className="size-6" />
          </button>
          <div className="flex-1 text-sm/6 font-semibold text-gray-900 dark:text-white">
            Dashboard
          </div>
          <a href="#">
            <span className="sr-only">Your profile</span>
            <UserButton>
              <UserButton.MenuItems>
                {!channel?.has_all_scopes && (
                  <UserButton.Action
                    label="Grant additional access"
                    labelIcon={<CheckBadgeIcon />}
                    onClick={onConnectClick}
                  />
                )}
                <UserButton.Action
                  label="Unlink YouTube Channel"
                  labelIcon={<TrashIcon />}
                  onClick={() => setShowRemoveChannelConfirm(true)}
                />
              </UserButton.MenuItems>
            </UserButton>
          </a>
        </div>

        <main className="py-10 lg:pl-72">
          <div className="px-4 sm:px-6 lg:px-8">
            {errorMessage && (
              <div className="mb-6 rounded-md bg-red-50 dark:bg-red-900/20 p-4 border border-red-200 dark:border-red-800">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <ExclamationTriangleIcon
                      className="h-5 w-5 text-red-400"
                      aria-hidden="true"
                    />
                  </div>
                  <div className="ml-3 flex-1">
                    <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                      {getErrorDetails().title}
                    </h3>
                    <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                      <p>{errorMessage}</p>
                    </div>
                  </div>
                  <div className="ml-3 flex-shrink-0">
                    <button
                      type="button"
                      className="inline-flex rounded-md bg-red-50 dark:bg-red-900/20 text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300 focus:outline-none"
                      onClick={dismissError}
                    >
                      <span className="sr-only">Dismiss</span>
                      <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                    </button>
                  </div>
                </div>
              </div>
            )}
            {children}
          </div>
        </main>
        {showRemoveChannelConfirm && (
          <RemoveChannelConfirm onConfirm={handleRemoveChannel} />
        )}
      </div>
    </>
  );
}

export default Sidebar;
